import { useMemo, useState } from "react";
import { getWorkspaceTimeZone } from "@/lib/utils";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ReferralCard } from "@/components/referral-card";
import { BrandNameNudgeBanner } from "@/components/brand-name-nudge-banner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { useToast } from "@/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Users,
  UserPlus,
  FileText,
  DollarSign,
  Calendar,
  TrendingUp,
  Clock,
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Sparkles,
  Target,
  BarChart3,
  Mail,
  Phone as PhoneIcon,
  Receipt,
  Grid3X3,
  CalendarDays,
  Music,
  MapPin,
  Phone,
  StickyNote,
  Activity,
  Banknote,
} from "lucide-react";
import { TutorialHint } from "@/components/tutorial-hint";
import { QuickActionCard } from "@/components/dashboard/quick-action-card";
import { FitnessRingCard } from "@/components/dashboard/fitness-ring";
import { FollowupsCard } from "@/components/dashboard/followups-card";
import { GamificationWidget, EndOfDaySummaryBanner } from "@/components/gamification/widget";
import { ManagerGamificationTile } from "@/components/gamification/manager-tile";
import MyProgressDashboard from "@/pages/sales-rep-dashboard";
import { MoodLogDialog } from "@/components/coach/mood-log-dialog";
import { TimecardControl } from "@/components/timecard/timecard-control";
import { useIsMobile } from "@/hooks/use-mobile";
import BackstageDashboard from "@/components/backstage/backstage-dashboard";
import { useAuth } from "@/hooks/use-auth";
import type { PlatformCustomer, SubscriptionInvoice } from "@shared/schema";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { UserRole, Lead, Client, Invoice, Region, UserProfile, Talent, TalentInvoice } from "@shared/schema";

interface UserWithProfile {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profile?: UserProfile;
}

interface UserActivityStats {
  userId: string;
  userName: string;
  leadsAdded: number;
  callsLogged: number;
  emailsLogged: number;
  notesAdded: number;
}

interface DashboardProps {
  userRole: UserRole;
  userId: string;
  isViewingAsUser?: boolean;
  actualUserRole?: UserRole;
}

interface SalesSummaryData {
  period: { start: string; end: string };
  metrics: {
    leadsCreated: { value: number; delta: number };
    clientsCreated: { value: number; delta: number };
    dollarsBooked: { value: number; delta: number };
    emailsSent: { value: number; delta: number };
    callsLogged: { value: number; delta: number };
  };
  quality?: {
    avgBookingValue: { value: number; delta: number };
    closeRate: { value: number; delta: number };
  };
}

interface WeekData {
  weekStart: string;
  bookedDollars: number;
  bookingCount: number;
}

interface RegionData {
  regionId: string;
  regionName: string;
  bookedDollars: number;
  bookingCount: number;
  avgBookingValue: number;
}

const RING_COLORS = {
  leads: "#22c55e",
  clients: "#3b82f6",
  dollars: "#f59e0b",
  emails: "#8b5cf6",
  calls: "#ec4899",
};

function formatRingCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value}`;
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendValue,
  className,
  testId,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  trend?: "up" | "down" | "neutral";
  trendValue?: string;
  className?: string;
  testId?: string;
}) {
  return (
    <Card className={className} data-testid={testId}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold tracking-tight" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
            {trend && trendValue && (
              <div className="flex items-center gap-1">
                {trend === "up" && (
                  <TrendingUp className="h-3 w-3 text-green-500" aria-hidden="true" />
                )}
                {trend === "down" && (
                  <TrendingUp className="h-3 w-3 text-red-500 rotate-180" aria-hidden="true" />
                )}
                <span className="sr-only">
                  {trend === "up"
                    ? "Trending up: "
                    : trend === "down"
                    ? "Trending down: "
                    : "Trend: "}
                </span>
                <span
                  className={`text-xs font-medium ${
                    trend === "up"
                      ? "text-green-500"
                      : trend === "down"
                      ? "text-red-500"
                      : "text-muted-foreground"
                  }`}
                >
                  {trendValue}
                </span>
              </div>
            )}
          </div>
          <div className="rounded-lg bg-primary/10 p-3">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="p-6 space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    </div>
  );
}

function ArtistDashboard({ userId }: { userId: string }) {
  const { data: myTalent, isLoading: talentLoading, error: talentError } = useQuery<Talent>({
    queryKey: ["/api/talent/by-user", userId],
    enabled: !!userId,
    retry: false,
  });

  // The talent's "shows" surface (Task #194) pivots on invoices instead
  // of clients: a single client can host multiple bookings on different
  // dates, so deriving from clients drops shows and shows the wrong
  // date. `/api/talent/:id/invoices` (`getInvoicesByTalent`) already
  // backfills `eventTime` from the linked client when the invoice has
  // none — same fallback that powers `/events`.
  const { data: talentInvoices = [], isLoading: invoicesLoading } = useQuery<TalentInvoice[]>({
    queryKey: ["/api/talent", myTalent?.id, "invoices"],
    enabled: !!myTalent?.id,
  });

  const isLoading = talentLoading || (!!myTalent && invoicesLoading);

  const invoices = talentInvoices;

  const shows = useMemo(() => {
    const now = new Date();
    const upcoming = invoices
      .filter((inv) => {
        const showDate = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
        return showDate && showDate >= now;
      })
      .sort((a, b) => {
        const dateA = a.dateOfEvent ? new Date(a.dateOfEvent).getTime() : 0;
        const dateB = b.dateOfEvent ? new Date(b.dateOfEvent).getTime() : 0;
        return dateA - dateB;
      });

    const thisWeek = upcoming.filter((inv) => {
      const showDate = new Date(inv.dateOfEvent!);
      const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      return showDate <= weekFromNow;
    });

    const thisMonth = upcoming.filter((inv) => {
      const showDate = new Date(inv.dateOfEvent!);
      return (
        showDate.getMonth() === now.getMonth() &&
        showDate.getFullYear() === now.getFullYear()
      );
    });

    return {
      upcoming,
      thisWeek,
      thisMonth,
      nextShow: upcoming[0] || null,
    };
  }, [invoices]);

  const revenueStats = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    const yearInvoices = invoices.filter(inv => {
      const showDate = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
      return showDate && showDate.getFullYear() === currentYear;
    });
    
    const upcomingInvoices = invoices.filter(inv => {
      const showDate = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
      return showDate && showDate >= now;
    });
    
    const totalRevenue = yearInvoices.reduce((sum, inv) => {
      const rate = parseFloat(inv.eventRate || "0");
      const discount = parseFloat(inv.discount || "0");
      return sum + (rate - discount);
    }, 0);
    
    const upcomingRevenue = upcomingInvoices.reduce((sum, inv) => {
      const rate = parseFloat(inv.eventRate || "0");
      const discount = parseFloat(inv.discount || "0");
      return sum + (rate - discount);
    }, 0);
    
    const paidInvoices = yearInvoices.filter(inv => inv.status === "paid");
    const paidRevenue = paidInvoices.reduce((sum, inv) => {
      const rate = parseFloat(inv.eventRate || "0");
      const discount = parseFloat(inv.discount || "0");
      return sum + (rate - discount);
    }, 0);
    
    return {
      totalRevenue,
      upcomingRevenue,
      paidRevenue,
      yearBookings: yearInvoices.length,
      upcomingBookings: upcomingInvoices.length,
    };
  }, [invoices]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (talentError || (!talentLoading && !myTalent)) {
    return (
      <div className="p-6 space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Talent Dashboard</h1>
          <p className="text-muted-foreground">Your upcoming performances and schedule</p>
        </div>
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium">Profile Not Linked</p>
                <p className="text-sm text-muted-foreground">
                  Your user account is not linked to an talent profile. Please contact your administrator to link your account.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatShowDate = (dateInput: Date | string) => {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Convert a show's event date into the `YYYY-MM-DD` string the run
  // sheet page expects in `?day=`. dateOfEvent is a calendar-day YYYY-MM-DD date
  // column — return the string directly to avoid UTC-midnight → previous-day drift
  // that comes from applying any tz, and to stay consistent with schedule.tsx.
  const dayParam = (date: Date | string) => {
    if (typeof date === "string") return date.slice(0, 10);
    const [y, mo, d] = date.toISOString().slice(0, 10).split("-");
    return `${y}-${mo}-${d}`;
  };

  // Talent dashboard "Location" line: prefer the structured
  // city/state backfilled from the linked client (Task #208) so we can
  // render the familiar "Austin, TX" form. Falls back to whatever side
  // is present, then to the raw concatenated `accountAddress`, and only
  // shows "TBD" when nothing at all is on the booking.
  const formatVenueLocation = (show: TalentInvoice): string => {
    const city = show.venueCity?.trim() || null;
    const state = show.venueState?.trim() || null;
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    const fallback = show.accountAddress?.trim();
    return fallback || "TBD";
  };

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Talent Dashboard</h1>
        <p className="text-muted-foreground">Your upcoming performances and schedule</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title="Upcoming Events"
          value={shows.upcoming.length}
          subtitle="Total scheduled"
          icon={Calendar}
          testId="metric-upcoming-shows"
        />
        <MetricCard
          title="This Week"
          value={shows.thisWeek.length}
          subtitle="Next 7 days"
          icon={Clock}
          testId="metric-this-week"
        />
        <MetricCard
          title="This Month"
          value={shows.thisMonth.length}
          subtitle={new Date().toLocaleDateString("en-US", { month: "long" })}
          icon={CalendarDays}
          testId="metric-this-month"
        />
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Revenue Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="card-upcoming-revenue">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
              <CardTitle className="text-sm font-medium">Upcoming Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">${revenueStats.upcomingRevenue.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">{revenueStats.upcomingBookings} upcoming bookings</p>
            </CardContent>
          </Card>
          <Card data-testid="card-year-revenue">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
              <CardTitle className="text-sm font-medium">{new Date().getFullYear()} Booked</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${revenueStats.totalRevenue.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">{revenueStats.yearBookings} events booked</p>
            </CardContent>
          </Card>
          <Card data-testid="card-paid-revenue">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
              <CardTitle className="text-sm font-medium">Paid Revenue</CardTitle>
              <Banknote className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">${revenueStats.paidRevenue.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Collected this year</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {shows.nextShow && (
        <Link
          href={`/runsheet?day=${dayParam(shows.nextShow.dateOfEvent!)}`}
          className="block"
          data-testid="link-next-performance"
        >
          <Card
            className="border-primary/20 bg-primary/5 hover-elevate active-elevate-2 cursor-pointer"
            data-testid="card-next-performance"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Next Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Date</p>
                    <p className="font-semibold text-lg">
                      {formatShowDate(shows.nextShow.dateOfEvent!)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Time</p>
                    <p className="font-medium" data-testid="text-next-show-time">
                      {shows.nextShow.eventTime ? `${shows.nextShow.eventTime} CT` : "TBD"}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Venue</p>
                    <p className="font-medium">{shows.nextShow.accountName || "TBD"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p
                      className="font-medium"
                      data-testid="text-next-show-location"
                    >
                      {formatVenueLocation(shows.nextShow)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      )}


      {shows.thisWeek.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>This Week's Shows</CardTitle>
            <CardDescription>Performances in the next 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {shows.thisWeek.map((show) => {
                const location = formatVenueLocation(show);
                const hasLocation = location !== "TBD";
                return (
                <Link
                  key={show.id}
                  href={`/runsheet?day=${dayParam(show.dateOfEvent!)}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover-elevate active-elevate-2 cursor-pointer"
                  data-testid={`row-this-week-show-${show.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <Calendar className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{show.accountName}</p>
                      {hasLocation && (
                        <p
                          className="text-sm text-muted-foreground"
                          data-testid={`text-this-week-location-${show.id}`}
                        >
                          {location}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatShowDate(show.dateOfEvent!)}</p>
                    <p
                      className="text-sm text-muted-foreground"
                      data-testid={`text-this-week-time-${show.id}`}
                    >
                      {show.eventTime ? `${show.eventTime} CT` : "Time TBD"}
                    </p>
                  </div>
                </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface BillingDashboardData {
  totalCollected: number;
  collectedCount: number;
  collectedChange: number;
  lateFeesCollectedPeriod: number;
  outstanding1_29: { amount: number; count: number };
  outstanding30_59: { amount: number; count: number };
  outstanding60Plus: { amount: number; count: number };
  outstandingAmount: number;
  outstandingCount: number;
  overdueAmount: number;
  overdueCount: number;
  lateFeesAssessed: number;
  lateFeesCollected: number;
  payTiming: {
    early: number;
    onTime: number;
    late: number;
  };
  recentPayments: Array<{
    id: string;
    accountName: string;
    amount: number;
    datePaid: string;
    paidByName: string | null;
  }>;
  revenueSeries: Array<{
    label: string;
    amount: number;
  }>;
  reminders: {
    dueToday: number;
    dueThisWeek: number;
    total: number;
  };
  period: string;
  periodLabel: string;
}

function BillingDashboard({ userId }: { userId: string }) {
  const [period, setPeriod] = useState<string>("month");
  const [isAddReminderOpen, setIsAddReminderOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>("");
  const [reminderDate, setReminderDate] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const { toast } = useToast();

  const { data: dashboardData, isLoading } = useQuery<BillingDashboardData>({
    queryKey: ["/api/billing-dashboard", period],
    queryFn: async () => {
      const response = await fetch(`/api/billing-dashboard?period=${period}`);
      if (!response.ok) throw new Error("Failed to fetch billing dashboard");
      return response.json();
    },
  });

  // Fetch unpaid invoices for reminder selection
  const { data: unpaidInvoices = [] } = useQuery<Invoice[]>({
    queryKey: ["/api/invoices"],
    select: (invoices) => invoices.filter(inv => inv.status !== "paid"),
  });

  const createReminderMutation = useMutation({
    mutationFn: async (data: { invoiceId: string; dueDate: string; note: string }) => {
      const response = await apiRequest("POST", "/api/invoice-followups", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      setIsAddReminderOpen(false);
      setSelectedInvoiceId("");
      setReminderDate("");
      setReminderNote("");
      toast({ title: "Reminder Created", description: "Your reminder has been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create reminder.", variant: "destructive" });
    },
  });

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const metrics = dashboardData || {
    totalCollected: 0,
    collectedCount: 0,
    collectedChange: 0,
    lateFeesCollectedPeriod: 0,
    outstanding1_29: { amount: 0, count: 0 },
    outstanding30_59: { amount: 0, count: 0 },
    outstanding60Plus: { amount: 0, count: 0 },
    outstandingAmount: 0,
    outstandingCount: 0,
    overdueAmount: 0,
    overdueCount: 0,
    lateFeesAssessed: 0,
    lateFeesCollected: 0,
    payTiming: { early: 0, onTime: 0, late: 0 },
    recentPayments: [],
    revenueSeries: [],
    reminders: { dueToday: 0, dueThisWeek: 0, total: 0 },
    period: "month",
    periodLabel: "This Month",
  };

  const totalPayTiming = metrics.payTiming.early + metrics.payTiming.onTime + metrics.payTiming.late;
  
  // Ring metrics configuration (matching sales dashboard style)
  const ringMetrics = [
    {
      key: "collected",
      value: metrics.collectedCount,
      delta: metrics.collectedChange,
      label: "Collected",
      color: "#22c55e",
      goal: Math.max(metrics.collectedCount * 1.2, 10),
      icon: <Users className="w-4 h-4" />,
    },
    {
      key: "lateFees",
      value: metrics.lateFeesCollectedPeriod,
      delta: 0,
      label: "Late Fees Collected",
      color: "#3b82f6",
      goal: Math.max(metrics.lateFeesCollectedPeriod, metrics.lateFeesAssessed, 100),
      icon: <PhoneIcon className="w-4 h-4" />,
      format: (v: number) => `$${v}`,
    },
    {
      key: "totalLateFees",
      value: metrics.lateFeesAssessed,
      delta: 0,
      label: "Total Late Fees",
      color: "#f59e0b",
      goal: Math.max(metrics.lateFeesAssessed * 1.2, 100),
      icon: <Target className="w-4 h-4" />,
      format: (v: number) => `$${v}`,
    },
    {
      key: "outstanding30_59",
      value: metrics.outstanding30_59.count,
      delta: metrics.outstanding30_59.count > 0 ? -100 : 0,
      label: "30-59 Days",
      color: "#ef4444",
      goal: Math.max(metrics.outstanding30_59.count * 1.2, 5),
      icon: <DollarSign className="w-4 h-4" />,
    },
    {
      key: "outstanding60Plus",
      value: metrics.outstanding60Plus.count,
      delta: metrics.outstanding60Plus.count > 0 ? -100 : 0,
      label: "60+ Days",
      color: "#dc2626",
      goal: Math.max(metrics.outstanding60Plus.count * 1.2, 5),
      icon: <Mail className="w-4 h-4" />,
    },
  ];

  // Format currency for chart
  const formatCurrency = (value: number): string => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with period selector */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Billing Dashboard</h1>
          <p className="text-muted-foreground">
            Your personal invoice and payment activity
          </p>
        </div>
        <div className="flex items-center gap-3">
          <TimecardControl />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="select-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main Content: Metrics Card + Reminders Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Left Column: My Progress Card + Revenue Chart */}
        <div className="space-y-6">
          {/* My Progress Card with Fitness Rings */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                My Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <FitnessRingCard metrics={ringMetrics} isLoading={isLoading} />
            </CardContent>
          </Card>

          {/* Revenue Collected Chart */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Revenue Collected
              </CardTitle>
              <Badge variant="secondary">
                ${metrics.totalCollected.toLocaleString()}
              </Badge>
            </CardHeader>
            <CardContent>
              {metrics.revenueSeries.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={metrics.revenueSeries}>
                    <defs>
                      <linearGradient id="colorCollected" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="label" className="text-xs" tick={{ fill: "var(--muted-foreground)" }} />
                    <YAxis
                      tickFormatter={(v) => formatCurrency(v)}
                      className="text-xs"
                      tick={{ fill: "var(--muted-foreground)" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "var(--popover)",
                        border: "1px solid var(--border)",
                        borderRadius: "8px",
                      }}
                      formatter={(value: number) => [`$${value.toLocaleString()}`, "Revenue"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#22c55e"
                      strokeWidth={2}
                      fill="url(#colorCollected)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No revenue data for this period</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Reminders + Recent Payments */}
        <div className="space-y-6">
          {/* Follow-up Reminders Panel */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Follow-up Reminders
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs"
                  onClick={() => setIsAddReminderOpen(true)}
                  data-testid="button-add-reminder"
                >
                  + Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-orange-50 dark:bg-orange-950/30 p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {metrics.reminders.dueToday}
                  </div>
                  <div className="text-xs text-muted-foreground">Due Today</div>
                </div>
                <div className="rounded-lg bg-green-50 dark:bg-green-950/30 p-4 text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {metrics.reminders.dueThisWeek}
                  </div>
                  <div className="text-xs text-muted-foreground">Due This Week</div>
                </div>
              </div>
              {metrics.reminders.total === 0 && (
                <div className="flex flex-col items-center justify-center py-4 text-center text-muted-foreground">
                  <Clock className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm">No reminders due today</p>
                  <p className="text-xs">Add a reminder to stay on track</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Payments */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Banknote className="w-4 h-4" />
                Recent Payments
              </CardTitle>
              <CardDescription className="text-xs">Latest invoices marked as paid</CardDescription>
            </CardHeader>
            <CardContent>
              {metrics.recentPayments.length > 0 ? (
                <div className="space-y-3">
                  {metrics.recentPayments.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                      <div>
                        <p className="font-medium text-sm">{payment.accountName}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(payment.datePaid).toLocaleDateString()}
                          {payment.paidByName && ` by ${payment.paidByName}`}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
                        ${payment.amount.toLocaleString()}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm py-4 text-center">No recent payments</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Payment Timing and Recent Payments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pay Timing Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Payment Timing
            </CardTitle>
            <CardDescription>How quickly clients pay after their show</CardDescription>
          </CardHeader>
          <CardContent>
            {totalPayTiming > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="flex-1">Early (Before Show)</span>
                  <span className="font-medium">{metrics.payTiming.early}</span>
                  <span className="text-muted-foreground text-sm w-12 text-right">
                    {Math.round((metrics.payTiming.early / totalPayTiming) * 100)}%
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="flex-1">On Time (Within 30 Days)</span>
                  <span className="font-medium">{metrics.payTiming.onTime}</span>
                  <span className="text-muted-foreground text-sm w-12 text-right">
                    {Math.round((metrics.payTiming.onTime / totalPayTiming) * 100)}%
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span className="flex-1">Late (30+ Days)</span>
                  <span className="font-medium">{metrics.payTiming.late}</span>
                  <span className="text-muted-foreground text-sm w-12 text-right">
                    {Math.round((metrics.payTiming.late / totalPayTiming) * 100)}%
                  </span>
                </div>
                <div className="h-2 flex rounded-full overflow-hidden bg-muted mt-4">
                  <div 
                    className="bg-green-500" 
                    style={{ width: `${(metrics.payTiming.early / totalPayTiming) * 100}%` }} 
                  />
                  <div 
                    className="bg-blue-500" 
                    style={{ width: `${(metrics.payTiming.onTime / totalPayTiming) * 100}%` }} 
                  />
                  <div 
                    className="bg-orange-500" 
                    style={{ width: `${(metrics.payTiming.late / totalPayTiming) * 100}%` }} 
                  />
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No payment data available</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Banknote className="w-5 h-5 text-primary" />
              Recent Payments
            </CardTitle>
            <CardDescription>Latest invoices marked as paid</CardDescription>
          </CardHeader>
          <CardContent>
            {metrics.recentPayments.length > 0 ? (
              <div className="space-y-3">
                {metrics.recentPayments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div>
                      <p className="font-medium text-sm">{payment.accountName}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(payment.datePaid).toLocaleDateString()}
                        {payment.paidByName && ` by ${payment.paidByName}`}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
                      ${payment.amount.toLocaleString()}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No recent payments</p>
            )}
          </CardContent>
        </Card>
      </div>


      {/* Add Reminder Dialog */}
      <Dialog open={isAddReminderOpen} onOpenChange={setIsAddReminderOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Add Follow-up Reminder</DialogTitle>
            <DialogDescription>
              Create a reminder for an unpaid invoice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invoiceSelect">Select Invoice</Label>
              <Select value={selectedInvoiceId} onValueChange={setSelectedInvoiceId}>
                <SelectTrigger data-testid="select-invoice">
                  <SelectValue placeholder="Select an invoice..." />
                </SelectTrigger>
                <SelectContent>
                  {unpaidInvoices.map((inv) => (
                    <SelectItem key={inv.id} value={inv.id}>
                      #{inv.invoiceNumber} - {inv.accountName} (${inv.eventRate})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reminderDate">Due Date</Label>
              <Input
                id="reminderDate"
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                data-testid="input-reminder-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reminderNote">Note (optional)</Label>
              <Textarea
                id="reminderNote"
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder="Add a note for this reminder..."
                rows={3}
                data-testid="input-reminder-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddReminderOpen(false);
                setSelectedInvoiceId("");
                setReminderDate("");
                setReminderNote("");
              }}
              data-testid="button-cancel-reminder"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedInvoiceId && reminderDate) {
                  createReminderMutation.mutate({
                    invoiceId: selectedInvoiceId,
                    dueDate: reminderDate,
                    note: reminderNote,
                  });
                }
              }}
              disabled={!selectedInvoiceId || !reminderDate || createReminderMutation.isPending}
              data-testid="button-save-reminder"
            >
              {createReminderMutation.isPending ? "Saving..." : "Save Reminder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type RevenueForecastResponse = {
  monthStart: string;
  monthEnd: string;
  bookedDollars: number;
  paidDollars: number;
  softBookedDollars: number;
  weightedPipelineDollars: number;
  forecastDollars: number;
  lastYearSameMonthBookedDollars: number;
  holdToPaidConversionRate: number;
};

function RevenueForecastCard() {
  const { data, isLoading } = useQuery<RevenueForecastResponse>({
    queryKey: ["/api/dashboard/forecast"],
  });

  const monthLabel = data?.monthStart
    ? new Date(data.monthStart).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: getWorkspaceTimeZone() })
    : "";
  const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
  const yoyDelta = data && data.lastYearSameMonthBookedDollars > 0
    ? ((data.bookedDollars - data.lastYearSameMonthBookedDollars) / data.lastYearSameMonthBookedDollars) * 100
    : null;

  return (
    <Card data-testid="card-revenue-forecast">
      <CardHeader>
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <CardTitle>Revenue Forecast</CardTitle>
        </div>
        <CardDescription>
          {monthLabel ? `Booked + soft-booked for ${monthLabel}` : "This month"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading || !data ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : (
          <>
            <div>
              <p className="text-3xl font-bold tracking-tight" data-testid="text-forecast-total">
                {fmt(data.forecastDollars)}
              </p>
              <p className="text-sm text-muted-foreground">
                Projected (booked + weighted soft-booked)
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">Booked</p>
                <p className="font-semibold" data-testid="text-forecast-booked">{fmt(data.bookedDollars)}</p>
                <p className="text-xs text-muted-foreground mt-1">{fmt(data.paidDollars)} collected</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">Soft-booked (hold)</p>
                <p className="font-semibold" data-testid="text-forecast-soft">{fmt(data.softBookedDollars)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {fmt(data.weightedPipelineDollars)} weighted @ {Math.round(data.holdToPaidConversionRate * 100)}%
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm border-t pt-3">
              <span className="text-muted-foreground">
                Last year, same month: {fmt(data.lastYearSameMonthBookedDollars)}
              </span>
              {yoyDelta !== null && (
                <Badge
                  variant={yoyDelta >= 0 ? "default" : "destructive"}
                  data-testid="badge-forecast-yoy"
                >
                  {yoyDelta >= 0 ? "+" : ""}{yoyDelta.toFixed(0)}% YoY
                </Badge>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

type RecentChangeEntry = {
  kind: "lead_created" | "client_created" | "invoice_updated";
  id: string;
  title: string;
  detail: string | null;
  status: string | null;
  occurredAt: string;
  actor: string | null;
};

function RecentlyChangedCard() {
  const { data = [], isLoading } = useQuery<RecentChangeEntry[]>({
    queryKey: ["/api/dashboard/recent-activity"],
  });

  const labelFor = (kind: RecentChangeEntry["kind"]) => {
    switch (kind) {
      case "lead_created": return "New lead";
      case "client_created": return "New client";
      case "invoice_updated": return "Invoice update";
    }
  };
  const hrefFor = (e: RecentChangeEntry) => {
    switch (e.kind) {
      case "lead_created": return `/leads?focus=${e.id}`;
      case "client_created": return `/clients?focus=${e.id}`;
      case "invoice_updated": return `/invoices?focus=${e.id}`;
    }
  };
  const relativeTime = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  return (
    <Card data-testid="card-recent-activity">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          <CardTitle>Recently Changed</CardTitle>
        </div>
        <CardDescription>Last 20 leads, clients, and invoice updates</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : data.length === 0 ? (
          <p className="text-muted-foreground text-sm py-6 text-center">
            No recent activity in the past 14 days.
          </p>
        ) : (
          <ul className="divide-y max-h-[420px] overflow-y-auto">
            {data.map((e) => (
              <li key={`${e.kind}-${e.id}`} className="py-2">
                <Link
                  href={hrefFor(e)}
                  className="flex items-start justify-between gap-3 hover:bg-muted/50 -mx-2 px-2 rounded"
                  data-testid={`row-recent-${e.kind}-${e.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline" className="text-xs">{labelFor(e.kind)}</Badge>
                      {e.status && <span className="capitalize">{e.status}</span>}
                      {e.actor && <span>· {e.actor}</span>}
                    </div>
                    <p className="text-sm font-medium truncate" data-testid={`text-recent-title-${e.id}`}>
                      {e.title}
                    </p>
                    {e.detail && <p className="text-xs text-muted-foreground">{e.detail}</p>}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                    {relativeTime(e.occurredAt)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AdminDashboard({ userId }: { userId: string }) {
  const [activityPeriod, setActivityPeriod] = useState<string>("month");

  // Headline-card numbers come from /api/dashboard/summary (workspace-wide
  // SQL aggregates). The previous client-side math summed /api/leads,
  // /api/clients, /api/invoices which (a) honored the per-user
  // hideBookedLeads pref — making "booked" leads invisible and forcing
  // conversion to 0% — and (b) was capped by pagination once a workspace
  // grew past one page. The dedicated endpoints excluded cancelled
  // invoices and bookings in cancelled runs from money/show counts so
  // the cards reflect real, active business.
  const { data: summary, isLoading: summaryLoading } = useQuery<{
    totalRevenue: number;
    collected: number;
    outstanding: number;
    activeLeads: number;
    totalClients: number;
    upcomingShows: number;
    thisMonthShows: number;
    totalInvoices: number;
    unpaidCount: number;
  }>({
    queryKey: ["/api/dashboard/summary"],
  });

  const { data: conversion } = useQuery<{
    rate: number;
    bookedFromLeads: number;
    totalLeadPool: number;
    scope: "workspace" | "user";
  }>({
    queryKey: ["/api/dashboard/conversion-rate"],
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
  });

  const { data: allUsers = [] } = useQuery<UserWithProfile[]>({
    queryKey: ["/api/users"],
  });

  const { data: activityStats = [], isLoading: activityLoading } = useQuery<UserActivityStats[]>({
    queryKey: ["/api/user-activity", activityPeriod],
    queryFn: async () => {
      const response = await fetch(`/api/user-activity?period=${activityPeriod}`);
      if (!response.ok) throw new Error("Failed to fetch activity stats");
      return response.json();
    },
  });

  const isLoading = summaryLoading;

  const metrics = useMemo(() => {
    return {
      totalRevenue: summary?.totalRevenue ?? 0,
      collected: summary?.collected ?? 0,
      outstanding: summary?.outstanding ?? 0,
      activeLeads: summary?.activeLeads ?? 0,
      totalClients: summary?.totalClients ?? 0,
      upcomingShows: summary?.upcomingShows ?? 0,
      thisMonthShows: summary?.thisMonthShows ?? 0,
      totalInvoices: summary?.totalInvoices ?? 0,
      unpaidCount: summary?.unpaidCount ?? 0,
      // One-decimal string for the "X% conversion" label. Falls back to
      // "0" while the conversion query is loading so the card doesn't
      // flicker NaN.
      conversionRate:
        conversion?.rate !== undefined ? conversion.rate.toFixed(1) : "0",
    };
  }, [summary, conversion]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Complete business overview and team activity
          </p>
        </div>
        <TimecardControl />
      </div>

      {/* Persistent nudge prompting admins to set the workspace brand
          name (Task #559). Self-hides when the field is set; admins are
          guaranteed to have brandingSettings permission so we can mount
          unconditionally inside AdminDashboard. */}
      <BrandNameNudgeBanner />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Year Revenue"
          value={`$${metrics.totalRevenue.toLocaleString()}`}
          subtitle={`$${metrics.collected.toLocaleString()} collected`}
          icon={DollarSign}
          testId="metric-year-revenue"
        />
        <MetricCard
          title="Outstanding"
          value={`$${metrics.outstanding.toLocaleString()}`}
          subtitle={`${metrics.unpaidCount} unpaid invoices`}
          icon={Clock}
          testId="metric-outstanding"
        />
        <MetricCard
          title="Active Leads"
          value={metrics.activeLeads}
          subtitle={`${metrics.conversionRate}% conversion`}
          icon={Target}
          testId="metric-active-leads"
        />
        <MetricCard
          title="Upcoming Events"
          value={metrics.upcomingShows}
          subtitle={`${metrics.thisMonthShows} this month`}
          icon={Calendar}
          testId="metric-upcoming-shows"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-100 dark:bg-green-900/30 p-2">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Clients</p>
                <p className="text-xl font-bold">{metrics.totalClients}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-100 dark:bg-blue-900/30 p-2">
                <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Invoices</p>
                <p className="text-xl font-bold">{metrics.totalInvoices}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-purple-100 dark:bg-purple-900/30 p-2">
                <MapPin className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Regions</p>
                <p className="text-xl font-bold">{regions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <ManagerGamificationTile enableToggle />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              <CardTitle>Team Activity</CardTitle>
            </div>
            <Select value={activityPeriod} onValueChange={setActivityPeriod}>
              <SelectTrigger className="w-[140px]" data-testid="select-activity-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Last 7 Days</SelectItem>
                <SelectItem value="month">Last 30 Days</SelectItem>
                <SelectItem value="year">Last Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <CardDescription>
            Track user activity: leads created, calls logged, emails sent, and notes added
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activityLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : activityStats.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No user activity found for this period.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Target className="h-4 w-4" />
                      <span>Leads</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Phone className="h-4 w-4" />
                      <span>Calls</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Mail className="h-4 w-4" />
                      <span>Emails</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <StickyNote className="h-4 w-4" />
                      <span>Notes</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activityStats
                  .filter(stat => stat.leadsAdded > 0 || stat.callsLogged > 0 || stat.emailsLogged > 0 || stat.notesAdded > 0)
                  .sort((a, b) => {
                    const totalA = a.leadsAdded + a.callsLogged + a.emailsLogged + a.notesAdded;
                    const totalB = b.leadsAdded + b.callsLogged + b.emailsLogged + b.notesAdded;
                    return totalB - totalA;
                  })
                  .map((stat) => {
                    const total = stat.leadsAdded + stat.callsLogged + stat.emailsLogged + stat.notesAdded;
                    return (
                      <TableRow key={stat.userId} data-testid={`activity-row-${stat.userId}`}>
                        <TableCell className="font-medium">{stat.userName}</TableCell>
                        <TableCell className="text-center">
                          {stat.leadsAdded > 0 ? (
                            <Badge variant="secondary">{stat.leadsAdded}</Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {stat.callsLogged > 0 ? (
                            <Badge variant="secondary">{stat.callsLogged}</Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {stat.emailsLogged > 0 ? (
                            <Badge variant="secondary">{stat.emailsLogged}</Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {stat.notesAdded > 0 ? (
                            <Badge variant="secondary">{stat.notesAdded}</Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-bold">{total}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RevenueForecastCard />
        <RecentlyChangedCard />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sales Overview</CardTitle>
          <CardDescription>Lead and booking management</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <QuickActionCard
              title="Leads"
              description="Manage sales leads"
              icon={Grid3X3}
              href="/leads"
              testId="link-leads"
              tutorialHintId="sidebar.leads"
            />
            <QuickActionCard
              title="Clients"
              description="View booked clients"
              icon={Users}
              href="/clients"
              testId="link-clients"
              tutorialHintId="sidebar.clients"
            />
            <QuickActionCard
              title="Communications"
              description="Email management"
              icon={Mail}
              href="/email-hub"
              testId="link-email"
              tutorialHintId="sidebar.emailHub"
            />
            <QuickActionCard
              title="Settings"
              description="System configuration"
              icon={Sparkles}
              href="/settings"
              testId="link-settings"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Financial Overview</CardTitle>
          <CardDescription>Invoices and payments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <QuickActionCard
              title="Invoices"
              description="Manage invoices"
              icon={FileText}
              href="/invoices"
              testId="link-invoices"
              tutorialHintId="sidebar.invoices"
            />
            <QuickActionCard
              title="Billing"
              description="Process payments"
              icon={Receipt}
              href="/billing"
              testId="link-billing"
            />
            <QuickActionCard
              title="Reports"
              description="Analytics dashboard"
              icon={BarChart3}
              href="/reports"
              testId="link-reports"
            />
            <QuickActionCard
              title="Master Schedule"
              description="View booking calendar"
              icon={CalendarDays}
              href="/master-schedule"
              testId="link-schedule"
              tutorialHintId="sidebar.masterSchedule"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MarketingDashboard({ userId }: { userId: string }) {
  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="dashboard-marketing">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Marketing Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Coming soon — campaign performance, audience growth, and engagement metrics will land here.
          </p>
        </div>
        <TimecardControl />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            What you can do today
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• Build and send email campaigns from the Communications module.</p>
          <p>• Manage drip sequences and SMS campaigns.</p>
          <p>• Use the Lead Gen module to expand your audience.</p>
          <p className="pt-2 text-xs">A dedicated marketing analytics view is on the roadmap.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// Task #102 Phase 3.5 — Clarik Operator Dashboard. Replaces the
// booking-CRM admin dashboard when the active workspace is `saas`
// (i.e. Clarik itself). Shows the platform-customer book + subscription
// invoice activity — what a SaaS operator actually wants to see — so
// signing into Clarik feels like a focused admin workspace, not a half-
// populated entertainment CRM.
type ClarikDashboardSummary = {
  totals: {
    total: number;
    active: number;
    trial: number;
    pastDue: number;
    canceled: number;
    churned: number;
    mrrCents: number;
  };
  recentCustomers: PlatformCustomer[];
  recentInvoices: SubscriptionInvoice[];
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function customerStatusBadgeClass(status: string): string {
  switch (status) {
    case "active": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "trial": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case "past_due": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    case "canceled":
    case "churned": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    default: return "bg-gray-100 text-gray-800";
  }
}

function invoiceStatusBadgeClass(status: string): string {
  switch (status) {
    case "paid": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "open": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case "draft": return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
    case "uncollectible":
    case "void": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    default: return "bg-gray-100 text-gray-800";
  }
}

function ClarikOperatorDashboard() {
  const { data, isLoading } = useQuery<ClarikDashboardSummary>({
    queryKey: ["/api/platform-dashboard/summary"],
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const totals = data?.totals ?? {
    total: 0, active: 0, trial: 0, pastDue: 0, canceled: 0, churned: 0, mrrCents: 0,
  };
  const recentCustomers = data?.recentCustomers ?? [];
  const recentInvoices = data?.recentInvoices ?? [];

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-clarik-operator">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-clarik-dashboard-title">
          Operator Dashboard
        </h1>
        <p className="text-muted-foreground">
          Clarik platform — customers, subscriptions, and billing activity.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={formatCents(totals.mrrCents)}
          subtitle={`${totals.active + totals.trial} active + trial`}
          icon={DollarSign}
          testId="metric-clarik-mrr"
        />
        <MetricCard
          title="Total Customers"
          value={totals.total}
          subtitle={`${totals.active} active`}
          icon={Users}
          testId="metric-clarik-total-customers"
        />
        <MetricCard
          title="Trialing"
          value={totals.trial}
          subtitle="Free trial workspaces"
          icon={Sparkles}
          testId="metric-clarik-trialing"
        />
        <MetricCard
          title="Past Due"
          value={totals.pastDue}
          subtitle={`${totals.churned} churned · ${totals.canceled} canceled`}
          icon={AlertTriangle}
          testId="metric-clarik-past-due"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="card-recent-customers">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Recent Signups</CardTitle>
              <Link href="/customers">
                <Button variant="ghost" size="sm" data-testid="link-all-customers">
                  All customers <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentCustomers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No platform customers yet. Convert a lead from the Leads tab.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">MRR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentCustomers.map((c) => (
                    <TableRow key={c.id} data-testid={`row-recent-customer-${c.id}`}>
                      <TableCell>
                        <Link href={`/customers/${c.id}`} className="hover:underline font-medium">
                          {c.companyName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge className={customerStatusBadgeClass(c.status)} variant="secondary">
                          {c.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCents(c.mrrCents || 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card data-testid="card-recent-invoices">
          <CardHeader>
            <CardTitle className="text-lg">Recent Subscription Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {recentInvoices.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No subscription invoices yet. They'll appear here once Stripe sends the first webhook.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentInvoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`row-recent-invoice-${inv.id}`}>
                      <TableCell className="font-mono text-xs">
                        {inv.hostedInvoiceUrl ? (
                          <a
                            href={inv.hostedInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline"
                          >
                            {inv.stripeInvoiceId}
                          </a>
                        ) : (
                          inv.stripeInvoiceId
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={invoiceStatusBadgeClass(inv.status)} variant="secondary">
                          {inv.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCents(inv.amountDueCents || 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function Dashboard({ userRole, userId, isViewingAsUser, actualUserRole }: DashboardProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        <BackstageDashboard userRole={userRole} userId={userId} />
        <MoodLogDialog />
      </>
    );
  }

  let inner: JSX.Element;
  // Task #113 — dropped the saas/admin → ClarikOperatorDashboard special
  // case. Every role on every workspace type now resolves to its
  // role-standard dashboard; saas-specific widgets live inside the
  // Super Admin Panel hub.
  switch (userRole) {
    case "artist":
      inner = <ArtistDashboard userId={userId} />;
      break;
    case "billing":
      inner = <BillingDashboard userId={userId} />;
      break;
    case "admin":
      inner = <AdminDashboard userId={userId} />;
      break;
    case "marketing":
      inner = <MarketingDashboard userId={userId} />;
      break;
    case "sales_rep":
    default:
      inner = <MyProgressDashboard effectiveUserId={userId} />;
  }

  return (
    <>
      {inner}
      <MoodLogDialog />
    </>
  );
}
